import type { Mitglied, Rolle, UmgebungDetail, UserInfo } from '@etown/shared';
import { request } from './api';

/** Verwaltungs-Aufrufe: je Umgebung (Admins) und global (Superadmins). */
export const adminApi = {
  // --- Umgebung ---
  mitglieder(umgebungId: string): Promise<{ mitglieder: Mitglied[] }> {
    return request(`/api/umgebungen/${encodeURIComponent(umgebungId)}/mitglieder`);
  },
  benutzerAnlegen(
    umgebungId: string,
    input: { username: string; displayName: string; password: string; rolle: Rolle },
  ): Promise<{ mitglied: Mitglied }> {
    return request(`/api/umgebungen/${encodeURIComponent(umgebungId)}/benutzer`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  rolleSetzen(umgebungId: string, userId: string, rolle: Rolle): Promise<{ mitglied: Mitglied }> {
    return request(`/api/umgebungen/${encodeURIComponent(umgebungId)}/mitglieder/${encodeURIComponent(userId)}`, {
      method: 'PUT',
      body: JSON.stringify({ rolle }),
    });
  },
  mitgliedAktualisieren(
    umgebungId: string,
    userId: string,
    patch: { displayName?: string; password?: string; isActive?: boolean },
  ): Promise<{ mitglied: Mitglied }> {
    return request(`/api/umgebungen/${encodeURIComponent(umgebungId)}/mitglieder/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  mitgliedEntfernen(umgebungId: string, userId: string): Promise<null> {
    return request(`/api/umgebungen/${encodeURIComponent(umgebungId)}/mitglieder/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  },

  // --- Superadmin ---
  superadminUsers(): Promise<{ users: UserInfo[] }> {
    return request('/api/superadmin/users');
  },
  superadminUmgebungen(): Promise<{ umgebungen: UmgebungDetail[] }> {
    return request('/api/superadmin/umgebungen');
  },
  superadminCreateUser(input: {
    username: string;
    displayName: string;
    password: string;
    isSuperadmin: boolean;
    umgebungId?: string;
    rolle?: Rolle;
  }): Promise<{ user: UserInfo }> {
    return request('/api/superadmin/users', { method: 'POST', body: JSON.stringify(input) });
  },
  superadminUpdateUser(
    id: string,
    patch: { displayName?: string; password?: string; isActive?: boolean; isSuperadmin?: boolean },
  ): Promise<{ user: UserInfo }> {
    return request(`/api/superadmin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
  },
  superadminDeleteUser(id: string): Promise<null> {
    return request(`/api/superadmin/users/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },

  // --- Karte (Admins der Umgebung) ---
  zoneAccess(mapId: string): Promise<{ zones: Array<{ zoneId: string; name: string; userIds: string[] }> }> {
    return request(`/api/admin/zone-access/${encodeURIComponent(mapId)}`);
  },
  setZoneAccess(mapId: string, zoneId: string, userIds: string[]): Promise<null> {
    return request(`/api/admin/zone-access/${encodeURIComponent(mapId)}/${encodeURIComponent(zoneId)}`, {
      method: 'PUT',
      body: JSON.stringify({ userIds }),
    });
  },
  /** Bild eines Objekts setzen (ohne Naehe-Check — fuer Kuehlschrank etc.). */
  async uploadObjectImage(mapId: string, objectId: string, file: File): Promise<{ imageId: string }> {
    const res = await fetch(
      `/api/admin/objects/${encodeURIComponent(mapId)}/${encodeURIComponent(objectId)}/image`,
      { method: 'POST', headers: { 'Content-Type': file.type }, credentials: 'same-origin', body: file },
    );
    const body = (await res.json().catch(() => null)) as { ok: boolean; data?: { imageId: string }; error?: string } | null;
    if (!res.ok || !body?.ok || !body.data) {
      throw new Error(body?.error ?? `Upload fehlgeschlagen (${res.status})`);
    }
    return body.data;
  },
  /** Objekt-Props aendern (z. B. TV-videoId, i-Point-URL). */
  setObjectProps(
    mapId: string,
    objectId: string,
    props: { videoId?: string; url?: string; label?: string },
  ): Promise<{ version: number }> {
    return request(`/api/admin/objects/${encodeURIComponent(mapId)}/${encodeURIComponent(objectId)}`, {
      method: 'PATCH',
      body: JSON.stringify(props),
    });
  },
};
